import { useCallback, useEffect, useState } from 'react';
import { Modal, Stack, Text } from '@adc/parallax-component-library';
import { AxiosError } from 'axios';

import { RichText } from '~/components/utility';
import { getDifferenceInMinutesFromNow } from '~/utilities/dateHelpers';
import i18n from '~/utilities/i18n';
import { logout } from '~/utilities/session';
import { getObject, removeObject, setObject } from '~/utilities/storage';

type StaticModalProps = Parameters<typeof Modal.show>['0'];

const translationsKeysData = {
  login: {
    lockoutMessageTitleKey: 'Login.modals.signInErrors.accountLocked',
    lockoutMessageSubtitleKey: 'Login.modals.signInErrors.accountLockedPeriod',
    authErrorTitleKey: 'Login.modals.signInErrors.loginFailure',
    authErrorAttemptsRemainingKey:
      'Login.modals.signInErrors.attemptsRemaining',
    authErrorLockedKey: 'Login.modals.signInErrors.accountWillBeLocked',
  },
  changepassword: {
    lockoutMessageTitleKey:
      'ChangePassword.microcopy.lockoutBanner.messageItem_1',
    lockoutMessageSubtitleKey:
      'ChangePassword.microcopy.lockoutBanner.messageItem_2',
    authErrorTitleKey:
      'ChangePassword.modals.changePasswordErrors.attemptsRemainingError.title',
    authErrorAttemptsRemainingKey:
      'ChangePassword.modals.changePasswordErrors.attemptsRemainingError.message',
    authErrorLockedKey:
      'ChangePassword.modals.changePasswordErrors.noAttemptsRemainingError.message',
  },
} as const;

const useAuth = (type?: 'changepassword' | 'login') => {
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutMessage, setLockoutMessage] = useState<string | undefined>(
    undefined,
  );
  const [lockoutSubmessage, setlockoutSubmessage] = useState<
    string | undefined
  >(undefined);
  const translationKeys = translationsKeysData[type ?? 'login'];

  const handleLockoutError = (error: AxiosError) => {
    const attemptsResetAfter = Number(
      error.response?.headers['x-attempts-reset-after'],
    );
    const attemptsResetAt = Number(
      error.response?.headers['x-attempts-reset-at'],
    );

    showLockoutMessage(attemptsResetAt, attemptsResetAfter);
  };

  const showLockoutMessage = useCallback(
    (attemptsResetAt: number, attemptsResetAfter: number) => {
      const { time, difference } =
        getDifferenceInMinutesFromNow(attemptsResetAt);

      if (difference < 0) {
        return;
      }

      setIsLocked(true);

      setLockoutMessage(i18n.t(translationKeys.lockoutMessageTitleKey));
      setlockoutSubmessage(
        i18n.t(translationKeys.lockoutMessageSubtitleKey, { time }),
      );

      const lockoutInterval = setInterval(() => {
        const { time: newTime, difference: newDifference } =
          getDifferenceInMinutesFromNow(attemptsResetAt);

        if (newDifference < 0) {
          clearInterval(lockoutInterval);
          setLockoutMessage(undefined);
          setlockoutSubmessage(undefined);
          setIsLocked(false);
          removeObject('loginLockout');
        } else {
          setlockoutSubmessage(
            i18n.t(translationKeys.lockoutMessageSubtitleKey, {
              time: newTime,
            }),
          );
        }
      }, 1000);

      setObject('loginLockout', {
        attemptsResetAt,
        lockoutInterval,
        attemptsResetAfter,
      } satisfies LoginLockout);
    },
    [
      translationKeys.lockoutMessageTitleKey,
      translationKeys.lockoutMessageSubtitleKey,
    ],
  );

  useEffect(() => {
    const loginLockout = getObject<LoginLockout>('loginLockout');

    if (loginLockout) {
      showLockoutMessage(
        loginLockout.attemptsResetAt,
        loginLockout.attemptsResetAfter,
      );
    }

    return () => {
      const loginLockoutCleanup = getObject<LoginLockout>('loginLockout');

      if (loginLockoutCleanup) {
        clearInterval(loginLockoutCleanup.lockoutInterval);
      }
    };
  }, [showLockoutMessage]);

  const showLoginFailure = useCallback(
    (
      attemptsRemaining = -1,
      attemptsResetAfter?: number,
      attemptsResetAt?: number,
      onCloseAfterLockout?: () => void,
      onClose?: () => void,
    ) => {
      const modalProps: StaticModalProps = {
        testID: 'Global.modals.loginFailure',
        header: '',
        primaryAction: {
          label: i18n.t('Global.microcopy.common.okCap'),
        },
        details: (
          <Stack gap="$4">
            <Text>{i18n.t(translationKeys.authErrorTitleKey)}</Text>
            {attemptsRemaining >= 0 ? (
              <Stack display="inline">
                <Text>
                  {i18n.t(translationKeys.authErrorAttemptsRemainingKey, {
                    count: attemptsRemaining,
                  })}
                </Text>
                <br />
                {attemptsResetAfter ? (
                  <Text fontWeight="$bodyBase.medium">
                    {i18n.t(translationKeys.authErrorLockedKey, {
                      time: attemptsResetAfter,
                      duration: attemptsResetAfter,
                    })}
                  </Text>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        ),
      };

      modalProps.primaryAction.onPress = () => {
        onClose?.();
      };

      if (attemptsResetAfter && attemptsResetAt) {
        modalProps.primaryAction.onPress = () => {
          showLockoutMessage(attemptsResetAt, attemptsResetAfter);
          onCloseAfterLockout?.();
        };
      }

      Modal.show(modalProps);
    },
    [
      showLockoutMessage,
      translationKeys.authErrorAttemptsRemainingKey,
      translationKeys.authErrorLockedKey,
      translationKeys.authErrorTitleKey,
    ],
  );

  const handleLoginError = useCallback(
    (
      error: AxiosError<ApiErrorData>,
      onCloseAfterLockout: () => void,
      onClose?: () => void,
    ) => {
      if (error.response?.data.details === 'Authorization.isRequired') {
        logout();

        return;
      }

      if (error.response?.headers['x-attempts-remaining']) {
        const attemptsRemaining = Number(
          error.response.headers['x-attempts-remaining'],
        );
        const attemptsResetAt = error.response.headers['x-attempts-reset-at']
          ? Number(error.response.headers['x-attempts-reset-at'])
          : 0;
        const attemptsResetAfter = error.response.headers[
          'x-attempts-reset-after'
        ]
          ? Number(error.response.headers['x-attempts-reset-after']) / 60
          : 0;

        showLoginFailure(
          attemptsRemaining,
          attemptsResetAfter,
          attemptsResetAt,
          onCloseAfterLockout,
          onClose,
        );

        return;
      }

      showLoginFailure();
    },
    [showLoginFailure],
  );

  const showHcpError = useCallback(() => {
    Modal.show({
      testID: 'Global.modals.blockHCPUser',
      header: '',
      details: (
        <Stack gap="$4" nativeID="Global.modals.blockHCPUser.body">
          <RichText
            text={i18n.t('Global.modals.blockHCPUser.body', {
              appName: i18n.t('Global.appNames.lactateView'),
            })}
          />
        </Stack>
      ),
      primaryAction: {
        label: i18n.t('Global.microcopy.common.okCap'),
      },
    });
  }, []);

  return {
    handleLoginError,
    handleLockoutError,
    showHcpError,
    isLocked,
    lockoutMessage,
    lockoutSubmessage,
  };
};

export default useAuth;
